import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

const AdminManagement = () => {
  const { currentUser } = useAuth();
  const [adminEmails, setAdminEmails] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Load admin emails from localStorage
  useEffect(() => {
    const storedAdmins = JSON.parse(localStorage.getItem('adminEmails') || '["fayedruet@gmail.com"]');
    setAdminEmails(storedAdmins);
  }, []);

  // Save admin emails to localStorage
  const saveAdminEmails = (emails) => {
    localStorage.setItem('adminEmails', JSON.stringify(emails));
    setAdminEmails(emails);
  };

  // Add new admin email
  const handleAddAdmin = (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      setError('Please enter a valid email address');
      return;
    }

    // Check if email already exists
    if (adminEmails.includes(newEmail)) {
      setError('This email is already an admin');
      return;
    }

    // Add email to admin list
    const updatedEmails = [...adminEmails, newEmail];
    saveAdminEmails(updatedEmails);
    setSuccess(`Successfully added ${newEmail} as admin`);
    setNewEmail('');

    // Clear success message after 3 seconds
    setTimeout(() => setSuccess(''), 3000);
  };

  // Remove admin email
  const handleRemoveAdmin = (emailToRemove) => {
    // Prevent removing the last admin
    if (adminEmails.length === 1) {
      setError('Cannot remove the last admin');
      setTimeout(() => setError(''), 3000);
      return;
    }

    // Prevent removing yourself
    if (emailToRemove === currentUser.email) {
      setError('You cannot remove yourself from admin list');
      setTimeout(() => setError(''), 3000);
      return;
    }

    const updatedEmails = adminEmails.filter(email => email !== emailToRemove);
    saveAdminEmails(updatedEmails);
    setSuccess(`Successfully removed ${emailToRemove} from admin list`);
    setTimeout(() => setSuccess(''), 3000);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            Admin Management
          </h1>
          <p className="text-gray-600">
            Manage admin access for the platform
          </p>
        </div>

        {/* Add New Admin Form */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Add New Admin
          </h2>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          {success && (
            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
              {success}
            </div>
          )}

          <form onSubmit={handleAddAdmin} className="space-y-4">
            <div>
              <label className="block text-gray-700 font-semibold mb-2">
                Gmail Address *
              </label>
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="example@gmail.com"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
              <p className="text-sm text-gray-500 mt-1">
                Enter the Gmail address of the user you want to make an admin
              </p>
            </div>

            <button
              type="submit"
              className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              + Add Admin
            </button>
          </form>
        </div>

        {/* Current Admin List */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Current Admins ({adminEmails.length})
          </h2>

          <div className="space-y-3">
            {adminEmails.map((email, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200"
              >
                <div className="flex items-center space-x-3">
                  <div className="bg-blue-100 text-blue-600 w-10 h-10 rounded-full flex items-center justify-center font-bold">
                    {email.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800">{email}</p>
                    {email === currentUser.email && (
                      <span className="text-xs text-blue-600 font-semibold">
                        (You)
                      </span>
                    )}
                    {email === 'fayedruet@gmail.com' && (
                      <span className="text-xs text-purple-600 font-semibold ml-2">
                        (Super Admin)
                      </span>
                    )}
                  </div>
                </div>

                <button
                  onClick={() => handleRemoveAdmin(email)}
                  disabled={adminEmails.length === 1 || email === currentUser.email}
                  className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                    adminEmails.length === 1 || email === currentUser.email
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-red-100 text-red-600 hover:bg-red-200'
                  }`}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>

          <div className="mt-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
            <h3 className="font-semibold text-yellow-800 mb-2">
              Important Notes:
            </h3>
            <ul className="text-sm text-yellow-700 space-y-1">
              <li>• Users must sign in with Google using the specified email to gain admin access</li>
              <li>• You cannot remove yourself from the admin list</li>
              <li>• At least one admin must remain in the system</li>
              <li>• Admin access is granted immediately upon next login</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminManagement;
